import Service, { service } from '@ember/service';
import { query } from '@warp-drive/utilities/json-api';
import {
  deriveSlrLoanStandingAction,
  deriveSlrLoanStandingStatus,
} from 'tio-employee/utils/slr-loan-standing-verification';
import {
  uploadSlrLoanStandingDocumentRequest,
  confirmSlrLoanStandingNameMatchRequest,
} from 'tio-data/requests/slr-loan-standing-verification';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type { EmployeePlanAccount } from 'tio-data/data/resources/employee-plan-account.type';
import type {
  SlrGoodStandingReason,
  SlrGoodStandingState,
  SlrLoanStandingAuditState,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

const GOOD_STANDING_STATES = new Set<SlrGoodStandingState>([
  'unknown',
  'eligible',
  'ineligible',
  'undeterminable',
]);

// Order a problem EPA is surfaced when the employee has several and none are
// eligible — most blocking first. (`processing` is client-only and never
// derived here, but listed for completeness.)
const ATTENTION_PRIORITY: SlrLoanStandingStatus[] = [
  'ineligible',
  'cannot_determine',
  'try_again',
  'pending_upload',
  'unknown',
  'processing',
];

export default class SlrLoanStandingVerificationService extends Service {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;

  /**
   * Read path (tio-front-end#8714): the signed-in employee's current
   * good-standing audit state, read from the JSON:API `employee_plan_accounts`
   * resource (auth-scoped to the user) — replacing the bespoke
   * `/people/:id/slr-loan-standing-verification/current` endpoint that never
   * existed.
   *
   * Account scoping (tio-front-end#9428): alerts/messaging relate only to the
   * account receiving employer contributions — EPAs on the employee's other
   * linked accounts are excluded server-side via `filter[account_id]`. The
   * scope id is the plain `accountReceivingContributionsId` person attribute,
   * which is serialized with the session person from `/me` (no accounts
   * include required — unlike the derived `accountReceivingContributions`
   * getter, which needs materialized accounts and goes null for paid-off /
   * deactivated accounts). When no receiving account exists, there is nothing
   * to message about: returns null WITHOUT falling back to other accounts'
   * EPAs.
   *
   * Multi-EPA aggregation (within the receiving account): if ANY in-scope EPA
   * is `eligible` we surface that; otherwise the most blocking attention
   * state. Returns null when there are no in-scope EPAs.
   *
   * Deliberately NOT filtered by employee_plan_id: the session exposes the
   * contribution plan (`currentEmployee.plan.id`), not the EmployeePlan
   * join-row id that `filter[employee_plan_id]` matches — sending the plan id
   * silently excluded every real EPA and the alert never fired (the
   * reliability bug this replaced). The account filter plus the API's
   * auth-scoping to the signed-in user is the load-bearing scope; multiple
   * EPAs on the account aggregate below.
   */
  fetchCurrentState = async (options?: { reload?: boolean }) => {
    const receivingAccountId = this.sessionContext.person?.accountReceivingContributionsId;
    if (!receivingAccountId) return null;

    const { content } = (await this.store.request(
      query<EmployeePlanAccount>(
        'employee-plan-account',
        {
          'filter[account_id]': receivingAccountId,
          // The account carries the reflected observation whose borrowerName
          // the eval compared — surfaced as documentName for the Review Name
          // panel (Figma v3).
          include: 'account',
        },
        // Post-write readers pass reload to bypass the soft-cache window —
        // the PATCH is SkipCache, so it never invalidates this query's
        // cached document.
        options?.reload ? { reload: true } : {}
      )
    )) as { content: { data: EmployeePlanAccount[] } };

    const person = this.sessionContext.person;
    const employerProvidedName =
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') || null;

    return aggregateAuditState(
      content.data.map((epa) => toAuditState(epa, { employerProvidedName }))
    );
  };

  // Upload path — blocked on a backend prerequisite (ObservableDocument needs an
  // EPA reference); see tio-front-end#8714. Left calling the not-yet-built
  // endpoints until that lands.
  createVerificationDocument = async (
    employeePlanAccountId: string,
    asset: DigitalAssetDetailSignature
  ) => {
    const { content } = (await this.store.request(
      uploadSlrLoanStandingDocumentRequest(employeePlanAccountId, asset)
    )) as { content: SlrLoanStandingAuditState };
    return content;
  };

  /**
   * Record the participant's answer to the fuzzy name-match prompt
   * (`name_match_confirmation_required`) by PATCHing `name_match_confirmation`
   * on the EPA (tio-api#8054). The PATCH re-evaluates good standing inline
   * server-side, but its response is rendered from the pre-eval record — so
   * refetch (reload: the PATCH is SkipCache and never invalidates the query's
   * cached document) for the settled state, falling back to deriving from the
   * PATCH response if the refetch comes back empty. That response is a RAW
   * JSON:API resource (`{ id, attributes }`), not a materialized record —
   * unwrap it before deriving.
   */
  confirmNameMatch = async (employeePlanAccountId: string, confirmed: boolean) => {
    const { content } = (await this.store.request(
      confirmSlrLoanStandingNameMatchRequest(
        employeePlanAccountId,
        confirmed ? 'confirmed' : 'denied'
      )
    )) as { content: { data: { id: string | null; attributes: Record<string, unknown> } } };

    const settled = await this.fetchCurrentState({ reload: true });
    if (settled) return settled;

    const raw = content.data;
    return toAuditState({
      id: raw.id,
      ...(raw.attributes as Partial<EmployeePlanAccount>),
    } as EmployeePlanAccount);
  };
}

function normalizeGoodStanding(value: string): SlrGoodStandingState {
  return GOOD_STANDING_STATES.has(value as SlrGoodStandingState)
    ? (value as SlrGoodStandingState)
    : 'unknown';
}

function toAuditState(
  epa: EmployeePlanAccount,
  context?: { employerProvidedName?: string | null }
): SlrLoanStandingAuditState {
  const goodStanding = normalizeGoodStanding(epa.goodStanding);
  const reason = (epa.goodStandingReason || null) as SlrGoodStandingReason | null;
  const observation = epa.account?.observation as Record<string, unknown> | undefined;
  // Observation-hash keys are dasherized on the wire (cf. account.ext reading
  // 'interest-weighted-average'); camel fallback covers demo fixtures.
  const documentName = observation?.['borrower-name'] ?? observation?.['borrowerName'];

  return {
    employeePlanAccountId: epa.id,
    status: deriveSlrLoanStandingStatus(goodStanding, reason),
    primaryReason: epa.goodStandingReason || null,
    action: deriveSlrLoanStandingAction(goodStanding, reason),
    featureEnabled: true,
    employerProvidedName: context?.employerProvidedName ?? null,
    documentName: typeof documentName === 'string' && documentName ? documentName : null,
  };
}

function aggregateAuditState(
  states: SlrLoanStandingAuditState[]
): SlrLoanStandingAuditState | null {
  if (states.length === 0) return null;

  const eligible = states.find((state) => state.status === 'eligible');
  if (eligible) return eligible;

  for (const status of ATTENTION_PRIORITY) {
    const match = states.find((state) => state.status === status);
    if (match) return match;
  }

  return states[0] ?? null;
}
